import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";

export default function Production({ embedded }) {
  const [data, setData] = useState(null);
  const [rotation, setRotation] = useState(null);
  const [entre, setEntre] = useState(null);
  const [entForm, setEntForm] = useState({ name: "", company: "", story: "" });
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("All");
  const [galleryCount, setGalleryCount] = useState(3);
  const load = () => {
    client.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
    client.get("/admin/rotation").then((r) => setRotation(r.data)).catch(() => {});
    client.get("/admin/entrepreneurs").then((r) => setEntre(r.data)).catch(() => {});
    client.get("/admin/settings").then((r) => setGalleryCount(r.data.gallery_image_count)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveEntre = async (list) => {
    try { const r = await client.put("/admin/entrepreneurs", { entrepreneurs: list }); setEntre(r.data); toast.success("Rotation saved."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save the rotation."); }
  };
  const moveEntre = (i, dir) => {
    const list = [...entre.entrepreneurs];
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    saveEntre(list);
  };
  const removeEntre = (i) => {
    if (!window.confirm(`Remove ${entre.entrepreneurs[i].name} from the rotation?`)) return;
    saveEntre(entre.entrepreneurs.filter((_, x) => x !== i));
  };
  const addEntre = () => {
    if (!entForm.name.trim()) { toast.error("Give them a name first."); return; }
    saveEntre([...entre.entrepreneurs, entForm]);
    setEntForm({ name: "", company: "", story: "" });
  };

  const saveGalleryCount = async (v) => {
    setGalleryCount(v);
    try { const r = await client.patch("/admin/settings", { gallery_image_count: v }); setGalleryCount(r.data.gallery_image_count); toast.success(v === 0 ? "AI image spreads OFF for new publishes (saves cost)." : `New publishes will auto-generate ${v} AI image(s).`); }
    catch { toast.error("Could not save setting."); }
  };

  const buildIssue = async () => {
    setBusy("build");
    try { const r = await client.post("/admin/issue/build", {}); toast.success(`Building the Newsroom set — ${r.data.pieces} drafts, one per category, closing on the Global Entrepreneur profile. Refresh in ~2 min, review in Editorial.`); }
    catch { toast.error("Build failed."); }
    setBusy("");
  };
  const archivePrev = async () => {
    if (!window.confirm("Archive everything not in the current issue? Content stays live at /archive — nothing is deleted.")) return;
    setBusy("archive");
    try { const r = await client.post("/admin/issue/archive-previous"); toast.success(`Archived ${r.data.archived} article(s).`); await load(); }
    catch { toast.error("Archive failed."); }
    setBusy("");
  };

  const t = data?.totals;
  const SEC_ORDER = ["Interview", "Culture", "Money", "Members' Desk"];
  const rows = (data?.rows || []).filter((r) => filter === "All" || r.section === filter);

  const actionButtons = (
    <div className="flex items-center gap-3">
      <button onClick={buildIssue} disabled={!!busy} className="bg-ink text-cream px-5 py-3 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="build-issue">
        {busy === "build" ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />} Build this month
      </button>
      <button onClick={archivePrev} disabled={!!busy} className="border border-ink/40 px-5 py-3 overline flex items-center gap-2 hover:border-ink transition-colors disabled:opacity-60" data-testid="archive-previous">
        {busy === "archive" ? <RefreshCw size={14} className="animate-spin" /> : <ArchiveIcon size={14} />} Archive last issue
      </button>
    </div>
  );
  return (
    <div>
      {!embedded && <Header kicker="The Magazine Engine" title="Production">{actionButtons}</Header>}
      <div className={embedded ? "py-8" : "p-6 sm:p-10"}>
        {embedded && <div className="mb-6 flex justify-end">{actionButtons}</div>}
        <div className="mb-8 border border-ink/15 bg-white p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="gallery-setting">
          <div>
            <p className="font-grotesk text-lg text-ink flex items-center gap-2"><ImageIcon size={16} /> AI image spread on publish</p>
            <p className="font-mono text-[11px] text-ink/50 mt-1">How many varied AI photos (venue · action · product) to auto-generate when a member is published. Lower = cheaper. 0 = off (use uploaded photos only).</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {[0, 1, 2, 3].map((n) => (
              <button key={n} onClick={() => saveGalleryCount(n)} className={`w-11 h-11 overline border transition-colors ${galleryCount === n ? "bg-ink text-cream border-ink" : "border-ink/30 hover:border-ink"}`} data-testid={`gallery-count-${n}`}>{n}</button>
            ))}
          </div>
        </div>
        {t && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px bg-ink/15 border border-ink/15 mb-8" data-testid="production-totals">
            <Stat label={`Issue ${data.current_issue}`} value={t.published} sub="live" />
            <Stat label="Drafts" value={t.draft} />
            <Stat label="Archived" value={t.archived} />
            {SEC_ORDER.map((s) => <Stat key={s} label={s} value={t.by_section?.[s] || 0} />)}
          </div>
        )}
        {rotation && (
          <div className="mb-8 border border-ink/15" data-testid="rotation-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 bg-ink text-cream">
              <span className="overline">Fairness Rotation · Fortnightly</span>
              <span className="font-mono text-xs text-cream/70">Cycle {rotation.cycle} → next issue {rotation.next_cycle} ({rotation.cycle_days}-day)</span>
            </div>
            <p className="px-5 pt-4 font-sans text-sm text-ink/60">Members ordered by who's waited longest. The engine features the top of this list next, alternating Interview ↔ Blog so no one is left behind.</p>
            <div className="grid grid-cols-12 gap-3 px-5 py-3 overline text-ink/50">
              <span className="col-span-1">#</span>
              <span className="col-span-5 sm:col-span-4">Member</span>
              <span className="col-span-3 hidden sm:block">Last featured</span>
              <span className="col-span-3 sm:col-span-2">Last</span>
              <span className="col-span-3 sm:col-span-2 text-right">Up next</span>
            </div>
            {rotation.members.map((r, i) => (
              <div key={r.member_id} className="grid grid-cols-12 gap-3 px-5 py-2.5 border-t border-ink/10 items-center" data-testid="rotation-row">
                <span className={`col-span-1 font-serif text-lg ${i === 0 ? "text-gloss-gold" : "text-ink/40"}`}>{i + 1}</span>
                <span className="col-span-5 sm:col-span-4 font-serif text-base truncate">{r.business_name}</span>
                <span className="col-span-3 hidden sm:block font-mono text-xs text-ink/50">{r.last_featured ? new Date(r.last_featured).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "never"}</span>
                <span className="col-span-3 sm:col-span-2 overline text-ink/60">{r.last_feature_kind || "—"}</span>
                <span className="col-span-3 sm:col-span-2 flex justify-end">
                  <span className={`overline px-2.5 py-1 ${r.next_kind === "interview" ? "bg-gloss-gold text-ink" : "bg-ink text-cream"}`}>{r.next_kind}</span>
                </span>
              </div>
            ))}
          </div>
        )}

        {entre && (
          <div className="mb-8 border border-ink/15" data-testid="entrepreneur-rotation-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 bg-ink text-cream">
              <span className="overline flex items-center gap-2"><Globe size={13} /> Global Entrepreneur · The Newsroom Closer</span>
              <span className="font-mono text-xs text-cream/70">Next up: {entre.entrepreneurs[entre.next_index]?.name || "—"}</span>
            </div>
            <p className="px-5 pt-4 font-sans text-sm text-ink/60">Every edition's ninth Newsroom piece profiles one of these founders, in this order. Reorder, remove or add your own — the next build follows this list.</p>
            <div className="px-5 py-4 space-y-2">
              {entre.entrepreneurs.map((e, i) => (
                <div key={`${e.name}-${i}`} className={`flex items-center gap-3 border px-4 py-2.5 ${i === entre.next_index ? "border-gloss-gold bg-gloss-gold/5" : "border-ink/10"}`} data-testid="entrepreneur-row">
                  <span className={`font-serif text-lg w-6 ${i === entre.next_index ? "text-gloss-gold" : "text-ink/40"}`}>{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-base truncate">{e.name} <span className="font-sans text-sm text-ink/50">· {e.company}</span>{i === entre.next_index && <span className="overline text-[9px] text-gloss-gold ml-2">NEXT EDITION</span>}</p>
                    {e.story && <p className="font-mono text-[10px] text-ink/40 truncate">{e.story}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => moveEntre(i, -1)} disabled={i === 0} className="p-1.5 border border-ink/20 hover:border-ink disabled:opacity-30 transition-colors" data-testid={`entre-up-${i}`}><ChevronUp size={13} /></button>
                    <button onClick={() => moveEntre(i, 1)} disabled={i === entre.entrepreneurs.length - 1} className="p-1.5 border border-ink/20 hover:border-ink disabled:opacity-30 transition-colors" data-testid={`entre-down-${i}`}><ChevronDown size={13} /></button>
                    <button onClick={() => removeEntre(i)} className="p-1.5 border border-ink/20 hover:border-red-600 hover:text-red-600 transition-colors" data-testid={`entre-remove-${i}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              <div className="border border-dashed border-ink/25 p-4 mt-3" data-testid="entrepreneur-add-form">
                <p className="overline text-[10px] text-ink/50 mb-3">Add a founder to the rotation</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <input value={entForm.name} onChange={(e) => setEntForm({ ...entForm, name: e.target.value })} placeholder="Name — e.g. Richard Branson" className="border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid="entre-add-name" />
                  <input value={entForm.company} onChange={(e) => setEntForm({ ...entForm, company: e.target.value })} placeholder="Company — e.g. Virgin" className="border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid="entre-add-company" />
                </div>
                <textarea value={entForm.story} onChange={(e) => setEntForm({ ...entForm, story: e.target.value })} rows={2} placeholder="Their story in one line — 'started a student magazine at 16 and built it into a global group…' (guides the writer)" className="w-full border border-ink/25 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-ink mb-3" data-testid="entre-add-story" />
                <button onClick={addEntre} className="bg-ink text-cream px-5 py-2.5 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="entre-add-btn"><Plus size={13} /> Add to rotation</button>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-5">
          {["All", ...SEC_ORDER].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`overline px-3 py-1.5 border transition-colors ${filter === s ? "bg-ink text-cream border-ink" : "border-ink/30 hover:border-ink"}`} data-testid={`prod-filter-${s.toLowerCase().replace(/[^a-z]/g, "")}`}>{s}</button>
          ))}
        </div>
        <div className="border border-ink/15" data-testid="production-table">
          <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-ink text-cream overline">
            <span className="col-span-6 sm:col-span-5">Title</span>
            <span className="col-span-3 sm:col-span-2">Section</span>
            <span className="col-span-3 sm:col-span-2 hidden sm:block">Type</span>
            <span className="col-span-2 hidden sm:block">Issue</span>
            <span className="col-span-3 sm:col-span-1 text-right">Status</span>
          </div>
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-3 px-5 py-3 border-t border-ink/10 items-center" data-testid="production-row">
              <span className="col-span-6 sm:col-span-5 font-serif text-base truncate">{r.title}{r.is_archived && <span className="overline text-ink/40 ml-2">archived</span>}</span>
              <span className="col-span-3 sm:col-span-2 font-mono text-xs text-ink/70">{r.section}</span>
              <span className="col-span-3 sm:col-span-2 hidden sm:block font-mono text-xs text-ink/50">{r.type}</span>
              <span className="col-span-2 hidden sm:block font-mono text-xs text-ink/50">{r.issue}</span>
              <span className="col-span-3 sm:col-span-1 flex justify-end"><Badge status={r.status} /></span>
            </div>
          ))}
          {rows.length === 0 && <p className="px-5 py-10 text-center font-sans text-ink/50">No articles in this section.</p>}
        </div>
      </div>
    </div>
  );
}

